import React, { useMemo, useState } from "react";
import {
  Badge,
  Box,
  Button,
  Callout,
  Card,
  Checkbox,
  Dialog,
  Flex,
  Heading,
  IconButton,
  Separator,
  Switch,
  Text,
  TextField,
} from "@radix-ui/themes";
import {
  ArrowRightIcon,
  CheckCircledIcon,
  ExclamationTriangleIcon,
  EyeClosedIcon,
  EyeOpenIcon,
  InfoCircledIcon,
} from "@radix-ui/react-icons";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";

import { useLocationContext, useProfileContext } from "context";
import { saveBrazeConfig, syncBraze } from "api/BrazeApi";
import { BrazeMark } from "./BrazeMark";

const STEPS = [
  { id: 1, label: "Connection" },
  { id: 2, label: "Data sync" },
  { id: 3, label: "Review" },
];

const PROFILE_FIELDS = [
  ["Email address", "email", true],
  ["First name", "first_name", true],
  ["Last name", "last_name", true],
  ["Phone number", "phone", true],
  ["Email marketing", "email_subscribe", true],
  ["Location IDs", "bconnect_location_ids", true],
  ["Age group", "customer_age_group", false],
  ["Gender", "gender", true],
];

function StepIndicator({ currentStep }) {
  return (
    <Flex align="center" justify="center" style={{ width: "100%" }}>
      {STEPS.map((step, index) => {
        const complete = step.id < currentStep;
        const active = step.id === currentStep;
        return (
          <React.Fragment key={step.id}>
            <Flex direction="column" align="center" gap="2" style={{ minWidth: 80 }}>
              <Flex
                align="center"
                justify="center"
                style={{
                  width: 30,
                  height: 30,
                  borderRadius: 999,
                  background: complete || active ? "var(--accent-9)" : "var(--gray-4)",
                  color: complete || active ? "white" : "var(--gray-10)",
                  border: active ? "3px solid var(--accent-4)" : "3px solid transparent",
                  boxSizing: "content-box",
                }}
              >
                {complete ? <CheckCircledIcon width="17" height="17" /> : <Text size="2" weight="bold">{step.id}</Text>}
              </Flex>
              <Text size="1" weight={active ? "medium" : "regular"} color={active ? undefined : "gray"}>
                {step.label}
              </Text>
            </Flex>
            {index < STEPS.length - 1 ? (
              <Box
                style={{
                  flex: 1,
                  minWidth: 12,
                  height: 2,
                  margin: "0 4px 22px",
                  background: step.id < currentStep ? "var(--accent-8)" : "var(--gray-5)",
                }}
              />
            ) : null}
          </React.Fragment>
        );
      })}
    </Flex>
  );
}

function SectionHeading({ title, description }) {
  return (
    <Flex direction="column" gap="1">
      <Heading size="4">{title}</Heading>
      <Text size="2" color="gray">{description}</Text>
    </Flex>
  );
}

export function BrazeSetupDialog({ isOpen, onOpenChange }) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { userProfile } = useProfileContext();
  const { locationsData = [] } = useLocationContext();
  const [step, setStep] = useState(1);
  const [showKey, setShowKey] = useState(false);
  const [apiKey, setApiKey] = useState("");
  const [endpoint, setEndpoint] = useState("");
  const [eventsEnabled, setEventsEnabled] = useState(false);
  const [selectedLocations, setSelectedLocations] = useState(() => new Set());
  const [optionalFields, setOptionalFields] = useState(() => new Set(["customer_age_group"]));
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState("");
  const [activated, setActivated] = useState(false);
  const [reconnectOpen, setReconnectOpen] = useState(false);
  const [reconnectAction, setReconnectAction] = useState("resume");

  const locations = useMemo(() => Array.isArray(locationsData) ? locationsData : [], [locationsData]);
  const allSelected = locations.length > 0 && selectedLocations.size === locations.length;
  const canContinueConnection = apiKey.trim().length > 0 && /^https:\/\/rest\..+\.braze\.(com|eu)\/?$/i.test(endpoint.trim());

  const toggleLocation = (id) => {
    setSelectedLocations((previous) => {
      const next = new Set(previous);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const toggleAllLocations = () => {
    setSelectedLocations(allSelected ? new Set() : new Set(locations.map((location) => location.id)));
  };

  const nextStep = () => setStep((value) => Math.min(3, value + 1));
  const previousStep = () => setStep((value) => Math.max(1, value - 1));

  const saveConnection = async (actionChoice = null) => {
    setIsSaving(true);
    setError("");
    try {
      const payload = {
        organisation_id: userProfile?.organisation_id,
        api_key: apiKey.trim(),
        rest_endpoint: endpoint.trim(),
      };
      if (actionChoice) payload.action_choice = actionChoice;
      const response = await saveBrazeConfig(payload);
      if (response.data?.status === "pending_user_choice") {
        setReconnectOpen(true);
        return;
      }
      if (response.data?.status !== "success") throw new Error(response.data?.message || "Connection failed.");
      if (actionChoice) setReconnectAction(actionChoice);
      nextStep();
    } catch (requestError) {
      setError(requestError.response?.data?.message || requestError.message || "Braze could not be connected.");
    } finally {
      setIsSaving(false);
    }
  };

  const activate = async () => {
    setIsSaving(true);
    setError("");
    try {
      const response = await saveBrazeConfig({
        // Connection validation happens on step one; activation adds the
        // location-specific sync settings chosen in the remaining steps.
        organisation_id: userProfile?.organisation_id,
        api_key: apiKey.trim(),
        rest_endpoint: endpoint.trim(),
        location_ids: Array.from(selectedLocations),
        events_enabled: eventsEnabled,
        field_mappings: {
          age: optionalFields.has("customer_age_group") ? "customer_age_group" : "",
        },
        action_choice: reconnectAction,
      });
      if (response.data?.status !== "success") throw new Error(response.data?.message || "Activation failed.");
      await syncBraze(userProfile?.organisation_id);
      setActivated(true);
      await queryClient.invalidateQueries(["braze-status", userProfile?.organisation_id]);
      onOpenChange(false);
      navigate("/integrations/active");
    } catch (requestError) {
      setError(requestError.response?.data?.message || requestError.message || "Braze could not be activated.");
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Dialog.Root open={isOpen} onOpenChange={(open) => !isSaving && onOpenChange(open)}>
      <Dialog.Content maxWidth="720px" maxHeight="85vh" style={{ overflowY: "auto" }}>
        <Flex align="center" gap="3" mb="2">
          <BrazeMark />
          <Dialog.Title mb="0">Connect Braze</Dialog.Title>
        </Flex>
        <Dialog.Description size="2" mb="4">
          Sync consented guest profiles and optional venue visits into your Braze workspace.
        </Dialog.Description>

        <Box mb="5" style={{ width: "100%" }}>
          <StepIndicator currentStep={step} />
        </Box>

        {reconnectOpen ? (
          <Flex direction="column" gap="4">
            <SectionHeading
              title="Reconnect Braze"
              description="Choose whether to sync data missed while Braze was disconnected."
            />
            <Flex direction="column" gap="2">
              <Button onClick={() => { setReconnectOpen(false); saveConnection("resume"); }} loading={isSaving}>
                Resume and sync missed data
              </Button>
              <Button variant="soft" onClick={() => { setReconnectOpen(false); saveConnection("continue_from_now"); }} loading={isSaving}>
                Start from now and skip missed data
              </Button>
              <Button variant="ghost" color="gray" onClick={() => setReconnectOpen(false)}>Back</Button>
            </Flex>
            {error ? <Callout.Root color="red"><Callout.Icon><ExclamationTriangleIcon /></Callout.Icon><Callout.Text>{error}</Callout.Text></Callout.Root> : null}
          </Flex>
        ) : (
          <>
          <Flex direction="column" gap="5">
            {step === 1 ? (
              <>
                <SectionHeading
                  title="Connect your workspace"
                  description="Create a dedicated REST API key in Braze and use the endpoint assigned to your workspace."
                />
                <Callout.Root color="blue" variant="surface">
                  <Callout.Icon><InfoCircledIcon /></Callout.Icon>
                  <Callout.Text>
                    <Flex direction="column" gap="2">
                      <Text size="2">
                        In Braze, go to <strong>Settings → APIs and Identifiers → REST API Keys</strong>,
                        then follow these steps:
                      </Text>
                      <Flex direction="column" gap="1" pl="3">
                        <Text size="2">
                          1. Click <strong>Create New API Key</strong> and enter a name for the key.
                        </Text>
                        <Text size="2">
                          2. Under <strong>API Permissions</strong>, expand <strong>User Data</strong>.
                        </Text>
                        <Text size="2">
                          3. Select <strong>users.track</strong> and <strong>users.export.ids</strong>.
                        </Text>
                        <Text size="2">
                          4. Click <strong>Create API Key</strong>, then copy the generated key and paste it below.
                        </Text>
                      </Flex>
                      <Text size="2">
                        <strong>users.track</strong> sends guest profiles and venue visits;
                        <strong> users.export.ids</strong> verifies access to the workspace. Without both,
                        connection validation or synchronization will fail.
                      </Text>
                    </Flex>
                  </Callout.Text>
                </Callout.Root>
                <Flex direction="column" gap="2">
                  <Text size="2" weight="medium">REST endpoint</Text>
                  <TextField.Root
                    value={endpoint}
                    onChange={(event) => setEndpoint(event.target.value)}
                    placeholder="https://rest.fra-01.braze.eu"
                    size="2"
                  />
                  <Text size="1" color="gray">Available in Braze under Settings → APIs and Identifiers.</Text>
                </Flex>
                <Flex direction="column" gap="2">
                  <Text size="2" weight="medium">REST API key</Text>
                  <TextField.Root
                    value={apiKey}
                    onChange={(event) => setApiKey(event.target.value)}
                    type={showKey ? "text" : "password"}
                    placeholder="Paste your dedicated API key"
                    size="2"
                  >
                    <TextField.Slot side="right">
                      <IconButton
                        type="button"
                        size="1"
                        variant="ghost"
                        color="gray"
                        aria-label={showKey ? "Hide API key" : "Show API key"}
                        onClick={() => setShowKey((value) => !value)}
                      >
                        {showKey ? <EyeClosedIcon /> : <EyeOpenIcon />}
                      </IconButton>
                    </TextField.Slot>
                  </TextField.Root>
                </Flex>
              </>
            ) : null}

            {step === 2 ? (
              <>
                <SectionHeading
                  title="Choose what Braze receives"
                  description="Profiles are the core sync. Venue visits remain optional and can be enabled later."
                />
                <Flex direction="column" gap="3">
                  <Card variant="surface">
                    <Flex align="center" gap="4">
                      <Flex direction="column" gap="1">
                        <Flex align="center" gap="2"><Text weight="medium">Guest profiles and consent</Text><Badge color="green">Required</Badge></Flex>
                        <Text size="1" color="gray">Identity, contact details, marketing consent and location attributes.</Text>
                      </Flex>
                    </Flex>
                  </Card>
                  <Card variant="surface">
                    <Flex justify="between" align="center" gap="4">
                      <Flex direction="column" gap="1">
                        <Flex align="center" gap="2"><Text weight="medium">Venue visit events</Text><Badge color="orange" variant="soft">Optional</Badge></Flex>
                        <Text size="1" color="gray">Send reliable WiFi sessions as the custom event “venue_visited”.</Text>
                      </Flex>
                      <Switch checked={eventsEnabled} onCheckedChange={setEventsEnabled} />
                    </Flex>
                  </Card>
                </Flex>

                <Separator size="4" />
                <Flex direction="column" gap="3">
                  <Text size="2" weight="medium">Profile fields</Text>
                  <Flex direction="column" gap="2">
                    {PROFILE_FIELDS.map(([source, destination, required]) => {
                      const checked = required || optionalFields.has(destination);
                      return (
                        <Flex key={destination} align="center" gap="3" py="2">
                          <Checkbox
                            checked={checked}
                            disabled={required}
                            onCheckedChange={() => {
                              setOptionalFields((previous) => {
                                const next = new Set(previous);
                                next.has(destination) ? next.delete(destination) : next.add(destination);
                                return next;
                              });
                            }}
                          />
                          <Text size="2" style={{ flex: 1 }}>{source}</Text>
                          <ArrowRightIcon color="var(--gray-8)" />
                          <Text size="2" color="gray" style={{ flex: 1, fontFamily: "monospace" }}>{destination}</Text>
                          {required ? <Badge size="1" color="gray" variant="soft">Core</Badge> : <Box style={{ width: 40 }} />}
                        </Flex>
                      );
                    })}
                  </Flex>
                </Flex>

                <Separator size="4" />
                <Flex direction="column" gap="3">
                  <Flex justify="between" align="center">
                    <Flex direction="column" gap="1">
                      <Text size="2" weight="medium">Locations included</Text>
                      <Text size="1" color="gray">Each location keeps its own incremental sync cursor.</Text>
                    </Flex>
                    <Button size="1" variant="ghost" onClick={toggleAllLocations}>{allSelected ? "Clear all" : "Select all"}</Button>
                  </Flex>
                  {locations.length ? (
                    <Flex wrap="wrap" gap="2">
                      {locations.map((location) => (
                        <Button
                          key={location.id}
                          size="2"
                          variant={selectedLocations.has(location.id) ? "soft" : "outline"}
                          color={selectedLocations.has(location.id) ? "accent" : "gray"}
                          onClick={() => toggleLocation(location.id)}
                        >
                          {selectedLocations.has(location.id) ? <CheckCircledIcon /> : null}
                          {location.name || location.label || `Location ${location.id}`}
                        </Button>
                      ))}
                    </Flex>
                  ) : (
                    <Callout.Root color="gray"><Callout.Icon><InfoCircledIcon /></Callout.Icon><Callout.Text>Locations will appear here after organisation data loads.</Callout.Text></Callout.Root>
                  )}
                </Flex>
              </>
            ) : null}

            {step === 3 ? (
              <>
                <SectionHeading
                  title="Review your setup"
                  description="Confirm the workspace, data types and locations before activating the sync."
                />
                <Flex direction="column" gap="3">
                  <Card variant="surface">
                    <Flex direction="column" gap="3">
                      <Flex justify="between"><Text size="2" color="gray">Workspace endpoint</Text><Text size="2" weight="medium">{endpoint || "Not entered"}</Text></Flex>
                      <Separator size="4" />
                      <Flex justify="between"><Text size="2" color="gray">Profile sync</Text><Badge color="green">Enabled</Badge></Flex>
                      <Flex justify="between"><Text size="2" color="gray">Venue events</Text><Badge color={eventsEnabled ? "green" : "gray"}>{eventsEnabled ? "Enabled" : "Disabled"}</Badge></Flex>
                      <Flex justify="between"><Text size="2" color="gray">Locations</Text><Text size="2" weight="medium">{selectedLocations.size || "None selected"}</Text></Flex>
                    </Flex>
                  </Card>
                  {activated ? <Callout.Root color="green"><Callout.Icon><CheckCircledIcon /></Callout.Icon><Callout.Text>Braze is connected and the first sync has been queued.</Callout.Text></Callout.Root> : null}
                </Flex>
              </>
            ) : null}
            {error ? <Callout.Root color="red"><Callout.Icon><ExclamationTriangleIcon /></Callout.Icon><Callout.Text>{error}</Callout.Text></Callout.Root> : null}
          </Flex>

          <Separator size="4" my="4" />
          <Flex justify="between" align="center" gap="3">
            <Button variant="soft" color="gray" onClick={step === 1 ? () => onOpenChange(false) : previousStep}>
              {step === 1 ? "Cancel" : "Back"}
            </Button>
            {step < 3 ? (
              <Button onClick={step === 1 ? () => saveConnection() : nextStep} loading={isSaving} disabled={(step === 1 && !canContinueConnection) || (step === 2 && selectedLocations.size === 0)}>
                Continue <ArrowRightIcon />
              </Button>
            ) : (
              <Button onClick={activate} loading={isSaving} disabled={activated || selectedLocations.size === 0}>{activated ? "Braze activated" : "Activate Braze"}</Button>
            )}
          </Flex>
          </>
        )}
      </Dialog.Content>
    </Dialog.Root>
  );
}
